
import { supabase } from '@/lib/supabase/client';

interface UseCustomerSelectionProps {
  setCustomerId: (id: string) => void;
  setCustomerName: (name: string) => void;
  setCustomerPhone: (phone: string) => void;
  setSelectedCustomer: (customer: any) => void;
  setShippingAddress: (address: string) => void;
  setCustomerEmail?: (email: string) => void;
  setCustomerCountry?: (country: string) => void;
  setCustomerRegion?: (region: string) => void;
  setCustomerMunicipality?: (municipality: string) => void;
}

export const useCustomerSelection = ({
  setCustomerId,
  setCustomerName,
  setCustomerPhone,
  setSelectedCustomer,
  setShippingAddress,
  setCustomerEmail,
  setCustomerCountry,
  setCustomerRegion,
  setCustomerMunicipality
}: UseCustomerSelectionProps) => {
  const handleSelectCustomer = async (customer: any) => {
    if (!customer) return;
    
    try {
      console.log("Selected customer full data:", customer);
      
      // Set basic customer info
      setCustomerId(customer.id);
      setCustomerName(customer.name || '');
      setCustomerPhone(customer.phone || '');

      // Set address if available
      if (customer.address) {
        setShippingAddress(customer.address);
      }
      
      // Set additional customer fields if the setters are provided
      if (setCustomerEmail) {
        setCustomerEmail(customer.email || '');
      }
      
      // Default country to Algeria if not provided
      const country = customer.country || 'Algeria';
      const region = customer.region || '';
      const municipality = customer.municipality || '';
      
      // Log the location values to debug
      console.log('Setting location fields:', {
        country,
        region,
        municipality,
        address: customer.address || ''
      });
      
      // Set location fields if setters are provided
      if (setCustomerCountry) {
        setCustomerCountry(country);
      }
      
      if (setCustomerRegion) {
        setCustomerRegion(region);
      }
      
      if (setCustomerMunicipality) {
        setCustomerMunicipality(municipality);
      }

      // Get order statistics for this customer
      const { data: orderData, error: orderError } = await supabase
        .from('orders')
        .select('id, total_amount')
        .eq('customer_id', customer.id);
        
      if (orderError) {
        console.error('Error fetching order data:', orderError);
      }
      
      // Get delivered/returned products counts
      let deliveredCount = 0;
      let returnedCount = 0;
      
      if (orderData && orderData.length > 0) {
        for (const order of orderData) {
          const { data: itemsData, error: itemsError } = await supabase
            .from('order_items')
            .select('quantity, status')
            .eq('order_id', order.id);
            
          if (itemsError) {
            console.error('Error fetching order items:', itemsError);
            continue;
          }
          
          if (itemsData) {
            for (const item of itemsData) {
              const quantity = item.quantity || 0;
              if (item.status === 'delivered') {
                deliveredCount += quantity;
              } else if (item.status === 'returned') {
                returnedCount += quantity;
              }
            }
          }
        }
      }
      
      // Calculate total spent
      const totalSpent = orderData 
        ? orderData.reduce((sum, order) => sum + (parseFloat(order.total_amount) || 0), 0)
        : 0;
      
      // Create an enhanced customer object with stats
      const enhancedCustomer = {
        ...customer,
        stats: {
          orders_count: orderData ? orderData.length : 0,
          total_spent: totalSpent,
          delivered_products: deliveredCount,
          returned_products: returnedCount
        }
      };
      
      // Set the enhanced customer object
      setSelectedCustomer(enhancedCustomer);
      
    } catch (error) {
      console.error('Error in handleSelectCustomer:', error);
    }
  };

  return { handleSelectCustomer };
};
